import React, { useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Undo2 } from "lucide-react";
import { useForm } from "react-hook-form";
import {
  DocumentFormData,
  ReservationUpdateFormData,
} from "./guests/utils/reservationSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useReservationSchemas } from "./hooks/useReservationSchemas";
import { useHotel } from "@/hooks/useHotel";
import { useReservation } from "@/hooks/useReservations";
import {
  mapReservationToUpdateFormData,
  type ReservationDetailsFormData,
} from "./utils/mapReservationToFormData";
import type { Reservation } from "@/types/reservation";
import { useReservationUpdate } from "./hooks/useReservationUpdate";
import { RequestType } from "@/types/request";
import { isSameDay } from "date-fns";
import { ReservationDetailTabs } from "@/components/pages/reservations/components/reservationDetails/ReservationDetailTabs";
import { determineGuestStatus } from "@/components/pages/reservations/guests/utils/guestStatusUtils";
import { ReservationInfo } from "@/components/pages/reservations/components/reservationDetails/ReservationInfo";
import { GuestInfo } from "@/components/pages/reservations/components/reservationDetails/GuestInfo";
import { GuestType } from "@/types/guest";
import { PaymentsInfo } from "@/components/pages/reservations/components/reservationDetails/PaymentsInfo";
import { RequestsInfo } from "@/components/pages/reservations/components/reservationDetails/RequestsInfo";
import { OtherInfo } from "@/components/pages/reservations/components/reservationDetails/OtherInfo";
import { getCurrentReservationPath } from "@/components/pages/reservations/utils/getCurrentReservationPath";
import { useTranslation } from "react-i18next";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { getGuestProfileNotes } from "@/components/pages/reservations/guests/utils/guestPreferences";

const noop = () => {};

export const EditReservationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  const backPath = useMemo(() => {
    return getCurrentReservationPath(location.pathname);
  }, [location.pathname]);

  const { data: reservation, isFetching: isLoadingReservation } =
    useReservation<ReservationDetailsFormData>(id!, (data: Reservation) => {
      return mapReservationToUpdateFormData(data);
    });

  const { isLoading: isLoadingHotel } = useHotel();
  const { handleReservationUpdate } = useReservationUpdate(id!);
  const { updateReservationSchema } = useReservationSchemas();
  const { t } = useTranslation();
  const { capabilities } = useAccessCapabilities();

  const [isEditing] = useState(!!searchParams.get("edit"));
  const [isLodedFormData, setIsLoadedFormData] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm<ReservationUpdateFormData>({
    resolver: zodResolver(updateReservationSchema),
    defaultValues: reservation,
  });

  useEffect(() => {
    if (reservation) {
      reset(reservation);
    }

    if (!isLoadingReservation) {
      setIsLoadedFormData(true);
    }
  }, [isLoadingReservation, reservation, reset]);

  const [roomRequests, guestRequests] = useMemo(
    () => [
      (reservation?.serviceRequests || []).filter(
        (request) => request.type !== RequestType.GUEST,
      ),
      (reservation?.serviceRequests || []).filter(
        (request) => request.type === RequestType.GUEST,
      ),
    ],
    [reservation],
  );

  const guestStatus = useMemo(
    () =>
      determineGuestStatus({
        preCheckinStatus: reservation?.preCheckinStatus,
        checkinStatus: reservation?.checkinStatus,
        preCheckoutStatus: reservation?.preCheckoutStatus,
        checkoutStatus: reservation?.checkoutStatus,
      }) || "",
    [reservation],
  );

  const roomServiceStatus = useMemo(() => {
    const roomRequests = reservation?.serviceRequests?.filter(
      (sr) => sr.type === RequestType.ROOM,
    );
    const done =
      (roomRequests?.length ?? 0) > 0 &&
      (roomRequests?.every((sr) => !!sr.dueDateTime) ?? false);

    return done ? "Cleaned" : "Not done";
  }, [reservation?.serviceRequests]);
  const arrivalDate = watch("arrivalDate");
  const departureDate = watch("departureDate");
  const documents = watch("documents");
  const additionalGuests = watch("additionalGuests");

  const guestsCount = useMemo(() => {
    let adults = 1;
    let children = 0;

    if (additionalGuests) {
      for (const g of additionalGuests) {
        if (g.guestType === GuestType.ADULT) adults += 1;
        if (g.guestType === GuestType.CHILD) children += 1;
      }
    }

    return { adults, children };
  }, [additionalGuests]);

  const initialGuestDisplay = reservation?.guest
    ? [reservation.guest.profile.firstName, reservation.guest.profile.lastName]
        .filter(Boolean)
        .join(" ")
    : undefined;

  const guestNotes = getGuestProfileNotes(
    reservation?.guest.profile.preferences,
  );

  let initialRoomDisplay: string | undefined;

  if (reservation?.room) {
    const categorySuffix = reservation.room.category?.name
      ? ` - ${reservation.room.category.name}`
      : "";

    initialRoomDisplay = `Room ${reservation.room.number}${categorySuffix}`;
  }

  const calculateNights = (arrival: string, departure: string): number => {
    const arrivalDate = new Date(arrival);
    const departureDate = new Date(departure);
    const timeDiff = departureDate.getTime() - arrivalDate.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  };

  const nights = useMemo(
    () => calculateNights(arrivalDate, departureDate),
    [arrivalDate, departureDate],
  );

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue("phone", value);
  };

  const handleDocumentSubmit = async (data: DocumentFormData) => {
    setValue("documents", [...(documents || []), data]);
  };

  const removeDocument = (documentId: number) => {
    setValue(
      "documents",
      documents?.filter((_, i) => i !== documentId),
    );
  };

  const handleDateChane = (type: "arrival" | "departure", date: Date) => {
    if (type === "arrival") {
      setValue("arrivalDate", date.toISOString() || "");

      if (isSameDay(departureDate, date)) {
        setValue(
          "departureDate",
          new Date(
            new Date().setDate(new Date(departureDate).getDate() + 1),
          ).toISOString(),
        );
      }
    }

    if (type === "departure") {
      setValue("departureDate", date.toISOString() || "");
    }
  };

  const onSubmit = async (data: ReservationUpdateFormData) => {
    await handleReservationUpdate(
      data,
      reservation!,
      null,
      reservation?.photoId,
    );
  };

  const requestedTab = (searchParams.get("tab") || "info") as
    | "info"
    | "guest"
    | "payments"
    | "requests"
    | "other";
  const currentTab =
    requestedTab === "requests" && !capabilities.canUseRequestsSystem
      ? "info"
      : requestedTab;

  if (isLoadingHotel && isLoadingReservation) {
    return <div>Loading...</div>;
  }

  if (!isLodedFormData) {
    return null;
  }

  return (
    <div className="relative min-h-screen p-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => (backPath ? navigate(backPath) : navigate(-1))}
        className="absolute top-8 left-8 flex items-center gap-2 font-medium"
      >
        <Undo2 className="h-4 w-4" />
        Back
      </Button>

      <div className="mx-auto mt-15 flex max-w-4xl flex-col gap-7">
        <ReservationDetailTabs reservationId={id!} />

        {currentTab === "payments" && id && <PaymentsInfo bookingId={id} />}

        {currentTab === "requests" && capabilities.canUseRequestsSystem && (
          <RequestsInfo
            bookingId={id!}
            guestId={reservation?.guestId}
            roomId={reservation?.room?.id}
            initialGuestDisplay={initialGuestDisplay}
            initialReservationDisplay={reservation?.reservationNumber}
            initialRoomDisplay={initialRoomDisplay}
            guestRequests={guestRequests}
            roomRequests={roomRequests}
            stub={noop}
          />
        )}

        {currentTab === "other" && (
          <OtherInfo
            bookingId={id}
            guestId={reservation?.guestId}
            notes={reservation?.notes}
            guestNotes={guestNotes}
          />
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {currentTab === "info" && (
            <ReservationInfo
              isEditing={isEditing}
              watch={watch}
              setValue={setValue}
              errors={errors}
              reservation={reservation}
              guestStatus={guestStatus}
              roomServiceStatus={roomServiceStatus}
              showRoomServiceStatus={capabilities.canUseRequestsSystem}
              nights={nights}
              onDateChange={handleDateChane}
              guestsCount={guestsCount}
            />
          )}

          {currentTab === "guest" && (
            <GuestInfo
              reservation={reservation}
              isEditing={isEditing}
              register={register}
              watch={watch}
              setValue={setValue}
              errors={errors}
              onPhoneChange={handlePhoneChange}
              onRemoveDocument={removeDocument}
              onDocumentSubmit={handleDocumentSubmit}
              guestNotes={guestNotes}
            />
          )}

          {isEditing && (currentTab === "info" || currentTab === "guest") && (
            <div className="mt-4 flex justify-end border-t border-neutral-200 pt-6">
              <Button
                type="submit"
                variant="new_default"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? t("reservation.buttons.updating")
                  : t("reservation.buttons.updateReservation")}
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
